import { InfoOutlined } from "@mui/icons-material";
import { Alert, Box, Button, MenuItem, Stack, Tooltip } from "@mui/material";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
// import InputMask from "react-input-mask";
import FormCheckbox from "../../../../../components/forms/form-checkbox/FormCheckbox";
import FormInput from "../../../../../components/forms/form-input/FormInput";
import FormPropertySection from "../../../../../components/forms/form-property-section/FormPropertySection";
import FormProperty from "../../../../../components/forms/form-property/FormProperty";
import FormSelect from "../../../../../components/forms/form-select/FormSelect";
import { StepMenuEditModeEnum } from "../../../../../components/step-menu/StepMenuState";
import { useUserContext } from "../../../../../contexts/user-context/UserContext";
import { useTimezones } from "../../../../../queries/time-zones/UseTimezones";
import i18n from "../../../../../services/localization-service/i18n";
import { UserFieldType } from "../UserFieldTypes";
import { useEffect, type JSX } from "react";
import { NewUserID } from "../UserEditorUtils";
import { useResetMfa } from "../../../../../queries/user-role-administration/UseResetMfa";
import { useQueryNotifications } from "../../../../../queries/UseQueryNotifications";
import { useRouteContext } from "../../../../../hooks/use-route-context/UseRouteContext";
import { useConfirmation } from "../../../../../contexts/confirmation-context/ConfirmationContext";
import { phoneNumberWithoutCountryCodeRegex } from "../../../../../common/regex/RegEx";
import { isNonUSEmail } from "../../../../../common/utilities/EmailUtils";

const jobFunctions: string[] = [
    i18n.t("profile.jobFunctions.admin"),
    i18n.t("profile.jobFunctions.assetManager"),
    i18n.t("profile.jobFunctions.eng"),
    i18n.t("profile.jobFunctions.engmanager"),
    i18n.t("profile.jobFunctions.investor"),
    i18n.t("profile.jobFunctions.it"),
    i18n.t("profile.jobFunctions.marketer"),
    i18n.t("profile.jobFunctions.opsmanager"),
    i18n.t("profile.jobFunctions.other"),
    i18n.t("profile.jobFunctions.prodForeman"),
    i18n.t("profile.jobFunctions.pumper"),
    i18n.t("profile.jobFunctions.workSuper")
];

export default function GeneralStep(): JSX.Element {

    const { t } = useTranslation();
    const routeContext = useRouteContext();
    const [userContext] = useUserContext();
    const { acknowledge } = useConfirmation();

    const {
        getValues,
        setValue,
        watch,
        trigger
    } = useFormContext<UserFieldType>();

    const user = getValues();
    const editMode = user.id === NewUserID
        ? StepMenuEditModeEnum.Create
        : StepMenuEditModeEnum.Edit;

    const { data: timezones } = useTimezones();

    const { mutateAsync: resetMfa } = useQueryNotifications(useResetMfa)({
        groupID: routeContext.groupID as number,
        onSuccess: (/*data*/) => {
            // save success            
            acknowledge(t("userEditor.steps.general.mfaResetSuccess", {name: user.firstName + " " + user.lastName}));
            setValue("mfaSource", "Email");
        }
    });



    const [
        firstName,
        lastName,
        phoneNumber,
        login,
        externalSignInName,
        allowSSO
    ] = watch([
        'firstName', 
        'lastName', 
        'phoneNumber', 
        'login',
        'externalSignInName',
        'allowSSO'
    ]);
    
    const nonUSEmail = login ? isNonUSEmail(login) : false;

    //console.log(`externalSignInName: ${externalSignInName}`);
    console.log(`nonUSEmail: ${nonUSEmail}`);

    async function onResetMfa() {
        await resetMfa(user.id);
    }

    function onPhoneChanged() {
        const val = getValues('phoneNumber');
        if (val?.trim().replace(/-/g, '') === '5555555555') {
            setValue('createSmsRecipient', false);
            setValue('createVoiceRecipient', false);

        }
    }
    
    // execute on every render
    useEffect(() => {

        setTimeout(() => {
            const items: ('firstName' | 'lastName' | 'phoneNumber' | 'login' | 'externalSignInName')[] = [];

            if ((firstName?.trim().length ?? 0) === 0) {
                items.push('firstName');
            }

            if ((lastName?.trim().length ?? 0) === 0) {
                items.push('lastName');
            }

            if ((phoneNumber?.trim().length ?? 0) === 0) {
                items.push('phoneNumber');
            }

            if ((login?.trim().length ?? 0) === 0) {
                items.push('login');
            }

            if (allowSSO && (externalSignInName?.trim().length ?? 0) === 0) {
                items.push('externalSignInName');
            }


            if (items.length) {
                trigger(items);
            }

        }, 1);
    });


    return (
        <Box
                sx={{
                    paddingLeft: 2,
                    paddingTop: 2
                }}
            >
            <FormPropertySection label={t("userEditor.steps.general.userInformation")}>

                <FormProperty>
                    <FormInput
                        ariaLabel="first name"
                        name="firstName"
                        label={t("userEditor.steps.general.firstName")}
                        sx={{ width: "100%" }}
                        rules={{
                            required: t("userEditor.steps.general.firstNameRequired")
                        }}
                    />
                </FormProperty>

                <FormProperty>
                    <FormInput
                        ariaLabel="last name"
                        name="lastName"
                        label={t("userEditor.steps.general.lastName")}
                        sx={{ width: "100%" }}
                        rules={{
                            required: t("userEditor.steps.general.lastNameRequired")
                        }}
                    />
                </FormProperty>

                <FormProperty>
                    <FormInput
                        ariaLabel="company name"
                        name="companyName"
                        label={t("userEditor.steps.general.companyName")}
                        sx={{ width: "100%" }}
                    />
                </FormProperty>

                <FormProperty>
                    <FormInput
                        ariaLabel="job title"
                        name="jobTitle"
                        label={t("userEditor.steps.general.jobTitle")}
                        sx={{ width: "100%" }}
                    />
                </FormProperty>

                <FormProperty>
                    <FormSelect
                        ariaLabel="job function"
                        name="jobFunction"
                        label={t("userEditor.steps.general.jobFunction")}
                        sx={{ width: "100%" }}
                    >
                        {jobFunctions.map((value, index) =>
                            <MenuItem key={`jf-${index}`} value={value}>
                                {value}
                            </MenuItem>
                        )}
                    </FormSelect>
                </FormProperty>

                <FormProperty>
                    <FormSelect
                        ariaLabel="timezone"
                        name="timeZoneIndex"
                        label={t("userEditor.steps.general.timeZone")}
                        sx={{ width: "100%" }}
                    >
                        {timezones?.map((o, index) =>
                            <MenuItem key={`tz-${index}`} value={o.id}>
                                {o.name}
                            </MenuItem>
                        )}
                    </FormSelect>
                </FormProperty>

            </FormPropertySection>
            <FormPropertySection label={t("userEditor.steps.general.accountInformation")}>

                <FormProperty>
                    <Stack direction='column'>

                        <FormInput
                            ariaLabel="login and contact email"
                            name="login"
                            label={t("userEditor.steps.general.login")}
                            rules={{
                                pattern: {
                                    value: /^[A-Za-z0-9+_-][A-Za-z0-9.+_-]{0,63}@[A-Za-z0-9.-]{1,61}\.[A-Za-z]{2,}$/,
                                    message: t('profile.invalidEmail')
                                },
                                required: t("userEditor.steps.general.emailRequired")
                            }}
                            sx={{ width: "100%" }}
                            readOnly={user.multipleCustomers}
                            endAdornment={
                                user.multipleCustomers === true
                                    ? <Tooltip title={t("userEditor.steps.general.multiCustomer")}>
                                        <InfoOutlined />
                                    </Tooltip>
                                    : undefined
                            }
                        />

            
                            {
                            nonUSEmail &&
                                <Alert severity="warning" sx={{marginTop: 2}}>
                                    Connections must be made from a US based network.
                                </Alert>
                            
                            }
                    </Stack>
                </FormProperty>

                {allowSSO && <FormProperty>
                    <FormInput
                        ariaLabel="external sign in name"
                        name="externalSignInName"
                        label={t("userEditor.steps.general.ssoNameLabel")}
                        sx={{ width: "100%" }}
                        //readOnly={!allowSSO}
                        rules={{
                            pattern: {
                                value: /^[A-Za-z0-9+_-][A-Za-z0-9.+_-]{0,63}@[A-Za-z0-9.-]{1,61}\.[A-Za-z]{2,}$/,
                                message: t('profile.invalidExternalSignInName')
                            }
                        }}
                    />
                </FormProperty>
                }

                {editMode === StepMenuEditModeEnum.Edit &&
                    <FormProperty>
                        <FormInput
                            ariaLabel="last login date and time"
                            name="lastLoginTimestamp"
                            label={t("userEditor.steps.general.lastLoginDateTime")}
                            sx={{ width: "100%" }}
                            readOnly={true}
                        />
                    </FormProperty>
                }




                {editMode === StepMenuEditModeEnum.Create &&
                    <FormProperty>
                        <FormCheckbox
                            name="createEmailRecipient"
                            label={t("userEditor.steps.general.createEmailRecipient")}
                            sx={{ width: "100%" }}
                        />
                    </FormProperty>
                }


                {editMode === StepMenuEditModeEnum.Create &&
                    <FormProperty>
                        <FormCheckbox
                            name="sendNewUserNotification"
                            label={t("userEditor.steps.general.emailWelcomeMessage")}
                            sx={{ width: "100%" }}
                        />
                    </FormProperty>
                }



                {editMode === StepMenuEditModeEnum.Edit && !user.disabled && userContext.user?.id !== user.id &&
                    <FormProperty>
                        <FormCheckbox
                            name="requirePasswordChange"
                            label={t("userEditor.steps.general.requirePasswordChange")}
                            sx={{ width: "100%" }}
                        />
                    </FormProperty>
                }

            </FormPropertySection>
            <FormPropertySection label={undefined}>

                <FormProperty>
                    <FormInput
                        ariaLabel="phone number"
                        name="phoneNumber"
                        label={t("userEditor.steps.general.phoneNumber")}
                        rules={{
                            pattern: {
                                // Bug 52761: DOC - Add User Error
                                value: phoneNumberWithoutCountryCodeRegex,
                                // value: /^\(\d{3}\) ?\d{3}( |-)?\d{4}|^\d{3}( |-)?\d{3}( |-)?\d{4}/,
                                message: `${t('profile.invalidPhone')}. e.g. (123) 456-7890, 123-456-7890, or 1234567890.`
                            },
                            required: t("userEditor.steps.general.phoneRequired")
                        }}
                        sx={{ width: "100%" }}
                        onChange={() => onPhoneChanged()}
                    >
                        {/* <InputMask mask="999-999-9999" maskPlaceholder=" " alwaysShowMask={true} /> */}
                    </FormInput>
                </FormProperty>

                {editMode === StepMenuEditModeEnum.Create &&
                    <FormProperty>
                        <FormCheckbox
                            name="createSmsRecipient"
                            label={t("userEditor.steps.general.createSmsRecipient")}
                            sx={{ width: "100%" }}
                        />
                    </FormProperty>
                }

                {editMode === StepMenuEditModeEnum.Create &&
                    <FormProperty>
                        <FormCheckbox
                            name="createVoiceRecipient"
                            label={t("userEditor.steps.general.createVoiceRecipient")}
                            sx={{ width: "100%" }}
                        />
                    </FormProperty>
                }                

            </FormPropertySection>
            {editMode === StepMenuEditModeEnum.Edit &&
                <>
                <FormPropertySection label={undefined}>
            
                    <>
                        {userContext.user?.id !== user.id &&
                            <FormProperty>
                                <FormCheckbox
                                    name="disabled"
                                    label={t("userEditor.steps.general.disableUser")}
                                    sx={{ width: "100%" }}
                                />
                            </FormProperty>
                        }

                        {/* {userContext.user?.isElynxAdmin &&
                            <FormProperty>
                                <FormCheckbox
                                    name="mobileAccessEnabled"
                                    label={t("userEditor.steps.general.enableMobileAccess")}
                                    sx={{ width: "100%" }}
                                />
                            </FormProperty>
                        } */}
                    </>
                

                </FormPropertySection>

                {user?.mfaEnabled && 
                <FormPropertySection label={undefined}>
                    <FormProperty>
                        
                        <FormInput
                            name="mfaSource"
                            label={t("userEditor.steps.general.mfaSource")}
                            readOnly={true}
                            sx={{ width: "100%" }}
                        >
                        </FormInput>

                    </FormProperty>
                    {user?.mfaSource === 'Authenticator' && 
                    <FormProperty sx={{ display: "flex", flexBasis: 'auto' }}>
                        <Button
                            sx={{
                                marginTop: 'auto',
                                marginBottom: 'auto',
                            }}
                            variant="text"
                            onClick={() => onResetMfa()}
                        >
                            {t('userEditor.steps.general.resetMfa')}
                        </Button> 

                    </FormProperty>
                    }
                </FormPropertySection>
                
                }
                </>
            }
        </Box>
    );
}
